/*
  Theme: the results page enhancement, shared by collection and search.

  RENAMED from ats-collection.js when the search results page was built. It was
  already generic: it keys on [data-col-grid], [data-col-more], [data-load-more],
  [data-col-sort], .pagination and data-section-id, none of which is specific to
  a collection. The one collection-specific line was the root selector at the
  bottom, which now matches both custom elements.

  THE PAGE ALREADY WORKS WITHOUT THIS FILE. The filter drawer is a real GET
  form, the sort menu is a native <details>, and pagination ships as Horizon's
  numbered ?page= links. Everything here is an upgrade on top of that, so a
  script failure leaves a working page rather than a broken one.

  Two jobs:

  1. LOAD MORE. The concept has a centred Load more button, Horizon has infinite
     scroll or numbered pages and no button. Rather than fork Horizon's
     paginated-list component, this fetches the next page down the same route
     its section renderer uses, appends the cards, and pushes the URL. The
     numbered nav is hidden once this takes over, and the button is revealed;
     ship order is the other way round so that no-JS gets the links.

  2. The sort <details> closing on an outside click and on Escape, which a bare
     <details> does not do.

  WHY NOT IMPORT HORIZON'S sectionRenderer. It is an ES module inside the
  theme's importmap, and every script we own is a classic deferred script. The
  request it makes is one line, `url.searchParams.set('section_id', id)` then
  fetch, so this takes the same route without dragging in the module graph. If
  Horizon ever changes that contract, this is the line to change.
*/
(function () {
  'use strict';

  var SECTION_PARAM = 'section_id';

  function parseCards(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    return {
      grid: doc.querySelector('[data-col-grid]'),
      more: doc.querySelector('[data-load-more]'),
    };
  }

  function init(root) {
    if (!root || root.dataset.coreReady === 'true') return;
    root.dataset.coreReady = 'true';

    var sectionId = root.dataset.sectionId;

    /* Swap the routes: the numbered nav was the shipped default so that a
       visitor with no JavaScript keeps real page links.

       THE TWO ARE SYMMETRIC AND UNCONDITIONAL. JavaScript on means Load more
       shown and the numbered nav hidden; JavaScript off means the reverse. An
       earlier version hid the nav only when a Load more button existed, which
       left the numbered links on the last page, where they were the one place
       in the flow they still appeared. On the last page with JavaScript there
       is now neither control, which is correct: the shopper has reached the end
       of the collection and the footer is immediately below.

       Hiding is done with the hidden attribute AND a CSS rule, because the
       attribute alone does nothing here. See the note on
       .core-results .pagination[hidden] in assets/core-results.css. */
    var moreWrap = root.querySelector('[data-col-more]');
    var pager = root.querySelector('.pagination');
    if (moreWrap) moreWrap.hidden = false;
    if (pager) pager.hidden = true;

    root.addEventListener('click', function (event) {
      var btn = event.target.closest('[data-load-more]');
      if (!btn || btn.disabled) return;

      var next = btn.dataset.nextUrl;
      if (!next) return;

      btn.disabled = true;
      var label = btn.textContent;

      var url = new URL(next, window.location.origin);
      var fetchUrl = new URL(url.toString());
      fetchUrl.searchParams.set(SECTION_PARAM, sectionId);

      fetch(fetchUrl.toString())
        .then(function (r) {
          if (!r.ok) throw new Error('section fetch failed: ' + r.status);
          return r.text();
        })
        .then(function (html) {
          var parsed = parseCards(html);
          var grid = root.querySelector('[data-col-grid]');
          if (!parsed.grid || !grid) throw new Error('no grid in response');

          /* Append rather than replace, so everything already loaded stays put
             and the scroll position does not jump. */
          while (parsed.grid.firstElementChild) {
            grid.appendChild(parsed.grid.firstElementChild);
          }

          /* The URL becomes the page just loaded, which is what Horizon's own
             paginated list does. A refresh then lands on that page rather than
             back at the top. */
          window.history.pushState({}, '', url.toString());

          if (parsed.more && parsed.more.dataset.nextUrl) {
            btn.dataset.nextUrl = parsed.more.dataset.nextUrl;
            btn.disabled = false;
            btn.textContent = label;
          } else {
            /* Last page reached. */
            if (moreWrap) moreWrap.hidden = true;
          }
        })
        .catch(function () {
          /* Put the button back and re-expose the numbered nav, so a failed
             fetch still leaves a way to reach page two. */
          btn.disabled = false;
          btn.textContent = label;
          if (pager) pager.hidden = false;
        });
    });

    /* The sort disclosure. Native <details> stays open on an outside click. */
    var sort = root.querySelector('[data-col-sort]');
    if (sort) {
      document.addEventListener('click', function (event) {
        if (!sort.open) return;
        if (sort.contains(event.target)) return;
        sort.open = false;
      });

      document.addEventListener('keydown', function (event) {
        if (event.key !== 'Escape' || !sort.open) return;
        sort.open = false;
        var summary = sort.querySelector('summary');
        if (summary) summary.focus();
      });
    }
  }

  function boot() {
    /* Both surfaces. <core-search> is the same machine with a different head. */
    var roots = document.querySelectorAll('core-collection, core-search');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
